/**
 * Centralized Status Management
 * Standardizes status vocabularies and naming conventions across all collections
 */

// Lead statuses — one board column per selectable status.
// CONVERTED is a special hidden status set by the convert-to-client flow;
// it is not part of the selectable enum and never renders on the board.
export const LEAD_STATUSES = {
  NEW: 'new',
  DISCOVERY: 'discovery',
  DEALMAKING: 'dealmaking',
  HOLD: 'hold',
  FROZEN: 'frozen',
  CONVERTED: 'converted'
};

// Pipeline order — drives board columns and selects
export const LEAD_STATUS_ORDER = [
  LEAD_STATUSES.NEW,
  LEAD_STATUSES.DISCOVERY,
  LEAD_STATUSES.DEALMAKING,
  LEAD_STATUSES.HOLD,
  LEAD_STATUSES.FROZEN
];

export const LEAD_STATUS_LABELS = {
  [LEAD_STATUSES.NEW]: 'New',
  [LEAD_STATUSES.DISCOVERY]: 'Discovery',
  [LEAD_STATUSES.DEALMAKING]: 'Deal-making',
  [LEAD_STATUSES.HOLD]: 'Hold',
  [LEAD_STATUSES.FROZEN]: 'Frozen',
  [LEAD_STATUSES.CONVERTED]: 'Converted'
};

// MUI palette keys (used by getStatusColor)
export const LEAD_STATUS_COLORS = {
  [LEAD_STATUSES.NEW]: 'info',
  [LEAD_STATUSES.DISCOVERY]: 'warning',
  [LEAD_STATUSES.DEALMAKING]: 'success',
  [LEAD_STATUSES.CONVERTED]: 'success'
};

// Board column accent colors
export const LEAD_STATUS_HEX = {
  [LEAD_STATUSES.NEW]: '#0288d1',
  [LEAD_STATUSES.DISCOVERY]: '#ed6c02',
  [LEAD_STATUSES.DEALMAKING]: '#2e7d32',
  [LEAD_STATUSES.HOLD]: '#607d8b',
  [LEAD_STATUSES.FROZEN]: '#90a4ae',
  [LEAD_STATUSES.CONVERTED]: '#2e7d32'
};

// Legacy status normalization. Existing docs may hold retired keys
// (first_meeting_set, heating_up, cooling_down, dead, making_offer,
// waiting_response, lost), label-cased values written by older code paths
// ('Converted', 'Heating Up', 'Deal-making'), or nothing at all.
// Normalized values are persisted lazily on the next save of each lead.
const LEGACY_LEAD_STATUS_MAP = {
  first_meeting_set: LEAD_STATUSES.NEW,
  heating_up: LEAD_STATUSES.NEW,
  cooling_down: LEAD_STATUSES.NEW,
  deal_making: LEAD_STATUSES.DEALMAKING,
  making_offer: LEAD_STATUSES.DEALMAKING,
  waiting_response: LEAD_STATUSES.DEALMAKING,
  lost: LEAD_STATUSES.FROZEN,
  dead: LEAD_STATUSES.FROZEN
};

export const normalizeLeadStatus = (raw) => {
  if (!raw) return LEAD_STATUSES.NEW;
  const key = String(raw).trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (key === LEAD_STATUSES.CONVERTED) return LEAD_STATUSES.CONVERTED;
  if (LEGACY_LEAD_STATUS_MAP[key]) return LEGACY_LEAD_STATUS_MAP[key];
  if (LEAD_STATUS_ORDER.includes(key)) return key;
  return LEAD_STATUSES.NEW;
};

// AR line communication statuses — team-communication only, independent of
// the AR line status. Partners may set the first three; Super Admins all.
export const AR_COMMUNICATION_STATUSES = {
  NEW: 'new',
  WAIT: 'wait',
  CHARGE: 'charge',
  INVOICED: 'invoiced',
  PUSHED: 'pushed',
  PULLED: 'pulled'
};

export const AR_COMMUNICATION_ORDER = [
  AR_COMMUNICATION_STATUSES.NEW,
  AR_COMMUNICATION_STATUSES.WAIT,
  AR_COMMUNICATION_STATUSES.CHARGE,
  AR_COMMUNICATION_STATUSES.INVOICED,
  AR_COMMUNICATION_STATUSES.PUSHED,
  AR_COMMUNICATION_STATUSES.PULLED
];

export const AR_COMMUNICATION_ADMIN_ONLY = [
  AR_COMMUNICATION_STATUSES.INVOICED,
  AR_COMMUNICATION_STATUSES.PUSHED,
  AR_COMMUNICATION_STATUSES.PULLED
];

export const AR_COMMUNICATION_LABELS = {
  [AR_COMMUNICATION_STATUSES.NEW]: 'New',
  [AR_COMMUNICATION_STATUSES.WAIT]: 'Wait',
  [AR_COMMUNICATION_STATUSES.CHARGE]: 'Charge',
  [AR_COMMUNICATION_STATUSES.INVOICED]: 'Invoiced',
  [AR_COMMUNICATION_STATUSES.PUSHED]: 'Pushed',
  [AR_COMMUNICATION_STATUSES.PULLED]: 'Pulled'
};

export const AR_COMMUNICATION_COLORS = {
  [AR_COMMUNICATION_STATUSES.NEW]: '#0288d1',
  [AR_COMMUNICATION_STATUSES.WAIT]: '#ed6c02',
  [AR_COMMUNICATION_STATUSES.CHARGE]: '#6a1b9a',
  [AR_COMMUNICATION_STATUSES.INVOICED]: '#2e7d32',
  [AR_COMMUNICATION_STATUSES.PUSHED]: '#00838f',
  [AR_COMMUNICATION_STATUSES.PULLED]: '#616161'
};

// Contract statuses
export const CONTRACT_STATUSES = {
  DRAFT: 'draft',
  ACTIVE: 'active',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
  EXPIRED: 'expired'
};

export const CONTRACT_STATUS_LABELS = {
  [CONTRACT_STATUSES.DRAFT]: 'Draft',
  [CONTRACT_STATUSES.ACTIVE]: 'Active',
  [CONTRACT_STATUSES.COMPLETED]: 'Completed',
  [CONTRACT_STATUSES.CANCELLED]: 'Cancelled',
  [CONTRACT_STATUSES.EXPIRED]: 'Expired'
};

// Payment statuses (for AR/AP)
export const PAYMENT_STATUSES = {
  SCHEDULED: 'scheduled',
  INVOICED: 'invoiced',
  PAID: 'paid',
  OVERDUE: 'overdue',
  WRITE_OFF: 'write_off',
  PARTIALLY_PAID: 'partially_paid',
  PENDING: 'pending',
  CANCELLED: 'cancelled'
};

export const PAYMENT_STATUS_LABELS = {
  [PAYMENT_STATUSES.SCHEDULED]: 'Scheduled',
  [PAYMENT_STATUSES.INVOICED]: 'Invoiced',
  [PAYMENT_STATUSES.PAID]: 'Paid',
  [PAYMENT_STATUSES.OVERDUE]: 'Overdue',
  [PAYMENT_STATUSES.WRITE_OFF]: 'Write-Off',
  [PAYMENT_STATUSES.PARTIALLY_PAID]: 'Partially Paid',
  [PAYMENT_STATUSES.PENDING]: 'Pending',
  [PAYMENT_STATUSES.CANCELLED]: 'Cancelled'
};

// Bank transaction statuses
export const BANK_TRANSACTION_STATUSES = {
  UNMATCHED: 'unmatched',
  MATCHED: 'matched',
  RECONCILED: 'reconciled',
  DISPUTED: 'disputed'
};

export const BANK_TRANSACTION_STATUS_LABELS = {
  [BANK_TRANSACTION_STATUSES.UNMATCHED]: 'Unmatched',
  [BANK_TRANSACTION_STATUSES.MATCHED]: 'Matched',
  [BANK_TRANSACTION_STATUSES.RECONCILED]: 'Reconciled',
  [BANK_TRANSACTION_STATUSES.DISPUTED]: 'Disputed'
};

// Banking types per UMA spec
export const BANK_TRANSACTION_TYPES = {
  INCOME: 'Income',
  EXPENSE: 'Expense',
  INTERNAL_TRANSFER: 'Internal Transfer',
  REFUND: 'Refund',
  CORRECTION: 'Correction'
};

// Banking entry methods per UMA spec
export const BANK_ENTRY_METHODS = {
  IMPORTED: 'Imported',
  MANUAL: 'Manual'
};

// Supported currencies (UMA uses USD for now)
export const CURRENCIES = {
  USD: 'USD'
};

// Entity statuses
export const ENTITY_STATUSES = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  SUSPENDED: 'suspended'
};

export const ENTITY_STATUS_LABELS = {
  [ENTITY_STATUSES.ACTIVE]: 'Active',
  [ENTITY_STATUSES.INACTIVE]: 'Inactive',
  [ENTITY_STATUSES.SUSPENDED]: 'Suspended'
};

// Proposal statuses
export const PROPOSAL_STATUSES = {
  DRAFT: 'draft',
  SENT: 'sent',
  VIEWED: 'viewed',
  ACCEPTED: 'accepted',
  REJECTED: 'rejected',
  EXPIRED: 'expired'
};

export const PROPOSAL_STATUS_LABELS = {
  [PROPOSAL_STATUSES.DRAFT]: 'Draft',
  [PROPOSAL_STATUSES.SENT]: 'Sent',
  [PROPOSAL_STATUSES.VIEWED]: 'Viewed',
  [PROPOSAL_STATUSES.ACCEPTED]: 'Accepted',
  [PROPOSAL_STATUSES.REJECTED]: 'Rejected',
  [PROPOSAL_STATUSES.EXPIRED]: 'Expired'
};

export const CONTRACT_STATUS_TRANSITIONS = {
  [CONTRACT_STATUSES.DRAFT]: [
    CONTRACT_STATUSES.ACTIVE,
    CONTRACT_STATUSES.CANCELLED
  ],
  [CONTRACT_STATUSES.ACTIVE]: [
    CONTRACT_STATUSES.COMPLETED,
    CONTRACT_STATUSES.CANCELLED,
    CONTRACT_STATUSES.EXPIRED
  ],
  [CONTRACT_STATUSES.COMPLETED]: [], // Final status
  [CONTRACT_STATUSES.CANCELLED]: [], // Final status
  [CONTRACT_STATUSES.EXPIRED]: [CONTRACT_STATUSES.ACTIVE] // Can be reactivated
};

// Utility functions
export const getStatusLabel = (status, statusType) => {
  switch (statusType) {
    case 'lead':
      return LEAD_STATUS_LABELS[status] || status;
    case 'contract':
      return CONTRACT_STATUS_LABELS[status] || status;
    case 'payment':
      return PAYMENT_STATUS_LABELS[status] || status;
    case 'bank_transaction':
      return BANK_TRANSACTION_STATUS_LABELS[status] || status;
    case 'entity':
      return ENTITY_STATUS_LABELS[status] || status;
    case 'proposal':
      return PROPOSAL_STATUS_LABELS[status] || status;
    default:
      return status;
  }
};

export const getStatusColor = (status, statusType) => {
  if (statusType === 'lead') {
    return LEAD_STATUS_COLORS[status] || 'default';
  }
  
  // Default color mapping for other types
  const positiveStatuses = ['active', 'paid', 'completed', 'accepted', 'reconciled'];
  const warningStatuses = ['pending', 'scheduled', 'invoiced', 'overdue', 'sent', 'viewed'];
  const errorStatuses = ['cancelled', 'dead', 'write_off', 'rejected', 'disputed'];
  
  if (positiveStatuses.includes(status)) return 'success';
  if (warningStatuses.includes(status)) return 'warning';
  if (errorStatuses.includes(status)) return 'error';
  return 'default';
};

export const isValidStatusTransition = (currentStatus, newStatus, statusType) => {
  switch (statusType) {
    case 'contract':
      return CONTRACT_STATUS_TRANSITIONS[currentStatus]?.includes(newStatus) || currentStatus === newStatus;
    default:
      return true; // Allow all transitions for other types
  }
}; 